/**
 * KidLoader — mascot with rotating messages and mood cycling
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MindoraCharacter, { type MindoraMood } from '../character/MindoraCharacter';
import { SparkleCluster } from '../decorations';

interface KidLoaderProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
}

const rotatingMessages = [
  'Thinking really hard...',
  'Looking through my books...',
  'Almost there...',
  'Finding something amazing...',
  'One more moment...',
];

const moodCycle: MindoraMood[] = ['thinking', 'idle', 'thinking', 'happy'];

export default function KidLoader({ message, size = 'lg' }: KidLoaderProps) {
  const [msgIndex, setMsgIndex] = useState(0);
  const [moodIndex, setMoodIndex] = useState(0);
  const isCustom = !!message;

  useEffect(() => {
    if (isCustom) return;
    const interval = setInterval(() => {
      setMsgIndex((i) => (i + 1) % rotatingMessages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isCustom]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMoodIndex((i) => (i + 1) % moodCycle.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const displayMessage = isCustom ? message : rotatingMessages[msgIndex];

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-8 relative">
      <SparkleCluster count={4} spread={40} className="absolute top-0 right-1/4" />
      <MindoraCharacter mood={moodCycle[moodIndex]} size={size} />
      <AnimatePresence mode="wait">
        <motion.p
          key={displayMessage}
          className="text-kid-text-soft font-kid text-kid-base font-bold"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
        >
          {displayMessage}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}
